import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useCallback, useRef, useState } from 'react';

import type { ModelLead } from '@app/api/Leads/types';
import type { ModelSalesFunnelStageLeads } from '@app/api/SalesFunnels';
import { useExchangeMutation } from '@app/api/SalesFunnelStageLead';
import {
  RIGHTS_IS_SALE_FUNNEL_CHANGE,
  RIGHTS_LEADS,
  SUCCESS,
} from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNewUserAccessRights } from '@app/hooks/useNewUserAccessRights';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import {
  applyStageLeadMoveTotals,
  findLead,
  findStageIdByLeadId,
  getIsBelowOverItem,
  parseLeadSortableId,
  relocateStageLeadForPreview,
  reorderStageLeadForPreview,
  resolveOverStageId,
  resolveTargetIndex,
} from '../utils';

import type {
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import type { Dispatch, SetStateAction } from 'react';

const POINTER_ACTIVATION_DISTANCE_PX = 4;

interface UseLeadsFunnelDndParams {
  stageLeads: ModelSalesFunnelStageLeads[];
  setStageLeads: Dispatch<SetStateAction<ModelSalesFunnelStageLeads[]>>;
}

export const useLeadsFunnelDnd = ({
  stageLeads,
  setStageLeads,
}: UseLeadsFunnelDndParams) => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { userAccessRights } = useNewUserAccessRights();
  const { showNotification } = useNotification();
  const [exchangeStage] = useExchangeMutation();

  const isDragEnabled =
    Boolean(ActiveBranchId) &&
    userAccessRights?.[RIGHTS_LEADS]?.[RIGHTS_IS_SALE_FUNNEL_CHANGE] === true;

  const [activeLead, setActiveLead] = useState<ModelLead | null>(null);
  const snapshotRef = useRef<ModelSalesFunnelStageLeads[] | null>(null);
  const originStageIdRef = useRef<number | null>(null);
  const pendingLeadIdsRef = useRef<Set<number>>(new Set());

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: POINTER_ACTIVATION_DISTANCE_PX },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const moveLead = useCallback(
    async (
      leadId: number,
      newFunnelStageId: number,
      rollbackStages: ModelSalesFunnelStageLeads[]
    ) => {
      if (!ActiveBranchId) return;

      pendingLeadIdsRef.current.add(leadId);

      try {
        await exchangeStage({
          ActiveBranchId,
          leadId,
          newFunnelStageId,
        }).unwrap();

        showNotification('Этап лида изменён', SUCCESS);
      } catch (error) {
        setStageLeads(rollbackStages);
        showNotification(error);
      } finally {
        pendingLeadIdsRef.current.delete(leadId);
      }
    },
    [ActiveBranchId, exchangeStage, setStageLeads, showNotification]
  );

  const dragStartHandler = useCallback(
    (event: DragStartEvent) => {
      const leadId = parseLeadSortableId(event.active.id);
      if (leadId === null) return;

      if (pendingLeadIdsRef.current.has(leadId)) return;

      snapshotRef.current = stageLeads;
      originStageIdRef.current = findStageIdByLeadId(stageLeads, leadId);
      setActiveLead(findLead(stageLeads, leadId));
    },
    [stageLeads]
  );

  const dragOverHandler = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      if (!over) return;

      const activeLeadId = parseLeadSortableId(active.id);
      if (activeLeadId === null) return;

      const activeStageId = findStageIdByLeadId(stageLeads, activeLeadId);
      const overStageId = resolveOverStageId(over, stageLeads);
      if (activeStageId === null || overStageId === null) return;

      const targetStage = stageLeads.find(
        (stage) => stage.StageId === overStageId
      );
      if (!targetStage) return;

      if (activeStageId === overStageId) {
        const stageLeadsList = targetStage.Leads ?? [];
        const fromIndex = stageLeadsList.findIndex(
          (lead) => lead.Id === activeLeadId
        );
        const toIndex = resolveTargetIndex(
          over.id,
          targetStage,
          getIsBelowOverItem(event)
        );

        if (fromIndex === -1 || fromIndex === toIndex) return;

        setStageLeads((prev) =>
          reorderStageLeadForPreview(prev, overStageId, fromIndex, toIndex)
        );
        return;
      }

      const toIndex = resolveTargetIndex(
        over.id,
        targetStage,
        getIsBelowOverItem(event)
      );

      setStageLeads((prev) =>
        relocateStageLeadForPreview(prev, activeLeadId, overStageId, toIndex)
      );
    },
    [setStageLeads, stageLeads]
  );

  const resetDragState = useCallback(() => {
    setActiveLead(null);
    snapshotRef.current = null;
    originStageIdRef.current = null;
  }, []);

  const dragEndHandler = useCallback(
    (event: DragEndEvent) => {
      const activeLeadId = parseLeadSortableId(event.active.id);
      const snapshot = snapshotRef.current;
      const originStageId = originStageIdRef.current;

      resetDragState();

      if (
        activeLeadId === null ||
        snapshot === null ||
        originStageId === null
      ) {
        return;
      }

      if (!event.over) {
        setStageLeads(snapshot);
        return;
      }

      const finalStageId = findStageIdByLeadId(stageLeads, activeLeadId);

      // Same stage: backend has no per-stage ordering, so do not persist a fake
      // order — restore the pre-drag arrangement.
      if (finalStageId === null || finalStageId === originStageId) {
        setStageLeads(snapshot);
        return;
      }

      setStageLeads((prev) =>
        applyStageLeadMoveTotals(prev, originStageId, finalStageId)
      );

      void moveLead(activeLeadId, finalStageId, snapshot);
    },
    [moveLead, resetDragState, setStageLeads, stageLeads]
  );

  const dragCancelHandler = useCallback(() => {
    const snapshot = snapshotRef.current;

    resetDragState();

    if (snapshot !== null) {
      setStageLeads(snapshot);
    }
  }, [resetDragState, setStageLeads]);

  return {
    activeLead,
    isDragEnabled,
    sensors,
    onDragStart: dragStartHandler,
    onDragOver: dragOverHandler,
    onDragEnd: dragEndHandler,
    onDragCancel: dragCancelHandler,
  };
};

export type LeadsFunnelDndModel = ReturnType<typeof useLeadsFunnelDnd>;
